import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  getVerifiedTotpFactorId,
  startTotpEnrollment,
  verifyTotpCode,
  type TotpEnrollment,
} from 'common/src/api/mfa';
import { BRANDS } from 'common/src/utils/branding';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, LoadingState, StatusChip } from '../ui';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEnabled: () => void;
};

type Step = 'loading' | 'error' | 'enabled' | 'disabled' | 'enrolling';

// Activar la verificación en dos pasos (app autenticadora). El menú lateral
// solo la ofrece a quien no la tiene; una vez activa no se desactiva desde la
// app (si se pierde el teléfono, un superadmin borra el factor en Supabase).
const TwoFactorModal = ({ isOpen, onClose, onEnabled }: Props) => {
  const { t } = useTranslation('security');
  const [step, setStep] = useState<Step>('loading');
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(() => {
    setStep('loading');
    setEnrollment(null);
    setCode('');
    setError(null);
    getVerifiedTotpFactorId()
      .then((id) => setStep(id ? 'enabled' : 'disabled'))
      .catch((err) => {
        // Sin saber si ya hay un factor no se ofrece activar: solo reintentar.
        console.error('Error loading MFA factors:', err);
        setError(
          t(
            'No pudimos cargar tu configuración de seguridad. Intenta de nuevo.'
          )
        );
        setStep('error');
      });
  }, [t]);

  useEffect(() => {
    if (isOpen) loadStatus();
  }, [isOpen, loadStatus]);

  const handleStart = async () => {
    setBusy(true);
    setError(null);
    try {
      // Emisor fijo FruitMarkets en ambos dominios: Authy busca el logo por
      // este nombre (con "CherryMarket" mostraba una imagen cualquiera).
      setEnrollment(await startTotpEnrollment(BRANDS.fruitmarkets.name));
      setStep('enrolling');
    } catch (err) {
      console.error('Error starting MFA enrollment:', err);
      setError(t('No pudimos iniciar la activación. Intenta de nuevo.'));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!enrollment || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotpCode(enrollment.factorId, code);
      setEnrollment(null);
      setStep('enabled');
      toast.success(t('Verificación en dos pasos activada'));
      onEnabled();
    } catch (err) {
      console.error('Error verifying MFA enrollment:', err);
      setError(
        t(
          'El código no es válido o ya expiró. Intenta con el código actual de tu app.'
        )
      );
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!busy}
      hideCloseButton={busy}
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader>{t('Verificación en dos pasos')}</ModalHeader>
        <ModalBody className='flex flex-col gap-4'>
          {error && (
            <Alert variant='danger' showIcon>
              {error}
            </Alert>
          )}

          {step === 'loading' && <LoadingState />}

          {step === 'enabled' && (
            <div className='flex flex-col items-start gap-2'>
              <StatusChip tone='success'>{t('Activada')}</StatusChip>
              <p className='text-sm text-default-500'>
                {t(
                  'Cada vez que inicies sesión te pediremos el código de 6 dígitos de tu app autenticadora.'
                )}
              </p>
              <p className='text-sm text-default-500'>
                {t(
                  'Si cambias o pierdes tu teléfono, pide a un administrador de CherryMarket que la restablezca.'
                )}
              </p>
            </div>
          )}

          {step === 'disabled' && (
            <div className='flex flex-col items-start gap-2'>
              <StatusChip tone='default'>{t('Desactivada')}</StatusChip>
              <p className='text-sm text-default-500'>
                {t(
                  'Agrega una segunda capa de seguridad: además de tu contraseña, al iniciar sesión te pediremos un código de una app autenticadora como Google Authenticator, Microsoft Authenticator o 1Password.'
                )}
              </p>
            </div>
          )}

          {step === 'enrolling' && enrollment && (
            <form
              id='two-factor-enroll'
              onSubmit={handleConfirm}
              className='flex flex-col gap-4'
            >
              <p className='text-sm text-default-500'>
                {t('1. Escanea este código QR con tu app autenticadora.')}
              </p>
              <img
                src={enrollment.qrCode}
                alt={t('Código QR para la app autenticadora')}
                // design-ignore: el QR necesita fondo blanco para que la app lo lea en modo oscuro
                className='h-48 w-48 self-center rounded-medium bg-white p-2'
              />
              <div className='flex flex-col gap-1 rounded-medium border border-default-200 p-4'>
                <p className='text-xs text-default-500'>
                  {t('¿No puedes escanearlo? Ingresa esta clave en la app:')}
                </p>
                <p className='select-all break-all font-mono text-sm'>
                  {enrollment.secret}
                </p>
              </div>
              <p className='text-sm text-default-500'>
                {t('2. Ingresa el código de 6 dígitos que muestra la app.')}
              </p>
              <Input
                label={t('Código')}
                value={code}
                onValueChange={(value) =>
                  setCode(value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode='numeric'
                pattern='[0-9]*'
                autoComplete='one-time-code'
                autoFocus
                maxLength={6}
              />
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose} isDisabled={busy}>
            {step === 'enrolling' ? t('Cancelar') : t('Cerrar')}
          </Button>
          {step === 'error' && (
            <Button color='primary' onPress={loadStatus}>
              {t('Reintentar')}
            </Button>
          )}
          {step === 'disabled' && (
            <Button color='primary' isLoading={busy} onPress={handleStart}>
              {t('Activar')}
            </Button>
          )}
          {step === 'enrolling' && (
            <Button
              type='submit'
              form='two-factor-enroll'
              color='primary'
              isLoading={busy}
              isDisabled={code.length !== 6}
            >
              {t('Confirmar')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TwoFactorModal;
